import React, { useState, useEffect } from "react"
import styled from "styled-components"
import { RowsContainer } from "component_library/components/react/FlexDiv"
import { Route, Routes, useLocation, useMatch } from "react-router-dom"
import Text from "component_library/components/react/Text"
import ChevronLeft from "icons/vectorLeft.svg?react"
import ChevronRight from "icons/vectorRight.svg?react"
import Button from "component_library/components/react/Button"
import { navigationLinks } from "./NavigationLinks"
import NavigationLink from "./NavigationLink"
import type ProductStatus from "DealerApp/types/ProductStatus"
import type Texts from "DealerApp/types/texts"
import SettingsNavigation from "./SettingsNavigation"
import Divider from "./Divider"
import HomeLink from "./HomeLink"

const NavigationWrapper = styled(RowsContainer)<{ collapsed: boolean }>`
  margin: none;
  position: sticky;
  width: ${({ collapsed }) => (collapsed ? 56 : 250)}px;
  height: 100vh;
  top: 0;
  left: 0;
  color: white;
  background: ${({ theme }) => theme.blue_900};
  transition: ${({ collapsed }) => (collapsed ? 0.5 : 0.5)}s;
  -webkit-box-shadow: 3px 0px 15px 0px ${({ theme }) => theme.gray_600};
  box-shadow: 3px 0px 15px 0px ${({ theme }) => theme.gray_600};
  z-index: 1;
`

// Styles chevron icons
const RightIcon = styled(ChevronRight)`
  fill: ${({ theme }) => theme.gray_500};
`

const LeftIcon = styled(ChevronLeft)`
  fill: ${({ theme }) => theme.gray_500};
`

// Set Button at the bottom of sidebar
const IconWrapper = styled.div`
  position: absolute;
  bottom: 0px;
  width: inherit;
  left: 0;
`

export const CollapseButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 0px 0px 16px;
  gap: 12px;
  isolation: isolate;

  width: 100%;
  height: 48px;

  border-radius: 0;
  background: ${({ theme }) => theme.blue_800};
  opacity: 96%;

  &:hover {
    background: linear-gradient(
      90deg,
      rgba(1, 172, 219, 0.3) 0%,
      rgba(1, 172, 219, 0) 100%
    );
  }
`

const CategoryContainer = styled(RowsContainer)`
  padding: 16px 16px 8px 16px;
`

export interface NavigationProps {
  dealerToolLinks: Record<string, string>
  texts: Texts
  productStatuses: Record<string, ProductStatus>
}

const Navigation = ({
  dealerToolLinks,
  texts,
  productStatuses,
}: NavigationProps) => {
  /** The navigation bar is wide-opened by default/not collapsed. */
  const [collapsed, setCollapsed] = useState(false)

  /** It's list of dealer links. */
  const linksArray = dealerToolLinks
    ? navigationLinks({
        dealerToolLinks,
        texts: texts.navigationBar,
        productStatuses,
      })
    : []

  /** Whenever the URL changes, the hook returns a new location object. */
  const { pathname } = useLocation()

  const isSettings = useMatch("/pro/settings/*")
  const isWebModules = useMatch("/pro/webmodules/*")
  const isLeadInsights = useMatch("/pro/lead-insights/*")

  /**
   * Currently only the settings page and lead insights page allows for the sidebar to always be expanded
   * Other pages/apps have constraints in terms of minimum available width.
   */
  const isAlwaysExpanded = isSettings || isWebModules || isLeadInsights

  /**
   * For small screen sizes, the navigation bar is collapsed
   * When on a settings page, we don't collapse the sidebar (we have more horizontal space available)
   */
  useEffect(() => {
    const isSmallScreen = window.innerWidth <= 1440
    isSmallScreen && !isAlwaysExpanded
      ? setCollapsed(true)
      : setCollapsed(false)
  }, [isAlwaysExpanded])

  return (
    <NavigationWrapper collapsed={collapsed} data-testid="navigation-root">
      <Routes>
        <Route
          path={"/pro/settings/*"}
          element={<SettingsNavigation sidebarCollapsed={collapsed} />}
        />

        <Route
          path={"/pro/webmodules/*"}
          element={<SettingsNavigation sidebarCollapsed={collapsed} />}
        />

        <Route
          path={"*"}
          element={
            <>
              <HomeLink
                link={dealerToolLinks?.homeLink}
                collapsed={collapsed}
              />

              <Divider margin="0px" />

              {linksArray.map(({ category, items }, index) => (
                <RowsContainer key={category}>
                  {items.every((value) => value.active === false) ? null : (
                    <>
                      {index !== 0 && <Divider margin="0px" />}
                      {!collapsed && category && (
                        <CategoryContainer>
                          <Text textType="smallText" fontColor="gray_400">
                            {category}
                          </Text>
                        </CategoryContainer>
                      )}
                    </>
                  )}
                  <RowsContainer>
                    {items.map((item) => (
                      <NavigationLink
                        {...item}
                        sidebarCollapsed={collapsed}
                        currentPathname={pathname}
                        key={item.label}
                      />
                    ))}
                  </RowsContainer>
                </RowsContainer>
              ))}

              <Divider margin="0px" />

              <IconWrapper>
                <Divider margin="1px 0px" />
                <CollapseButton
                  onClick={() => setCollapsed(!collapsed)}
                  borderColor="none"
                  data-testid="collapse-expand-button"
                >
                  {collapsed ? (
                    <RightIcon />
                  ) : (
                    <>
                      <LeftIcon />
                      {texts.navigationBar.labels.collapse}
                    </>
                  )}
                </CollapseButton>
              </IconWrapper>
            </>
          }
        />
      </Routes>
    </NavigationWrapper>
  )
}

export default Navigation
